/**
 * Severity Configuration
 *
 * Centralized severity level configuration for notification thresholds.
 * Used by 15+ integrations (Slack, Email, Teams, Webhook, etc.)
 */

import type {
  SeverityLevel,
  SeverityOption,
} from '@/features/integrations/types/configuration.types';

/**
 * Severity levels in order of criticality (lowest to highest)
 */
export const SEVERITY_LEVELS = ['I', 'L', 'M', 'H', 'C'] as const;

/**
 * Severity level labels
 */
export const SEVERITY_LABELS: Record<string, string> = {
  I: 'Info',
  L: 'Low',
  M: 'Medium',
  H: 'High',
  C: 'Critical',
};

/**
 * Severity threshold options for select dropdowns
 * Each value represents a cumulative threshold (e.g., 'MHC' = Medium + High + Critical)
 */
export const SEVERITY_OPTIONS: SeverityOption[] = [
  {
    label: 'Critical',
    value: 'C',
  },
  {
    label: 'High',
    value: 'HC',
  },
  {
    label: 'Medium',
    value: 'MHC',
  },
  {
    label: 'Low',
    value: 'LMHC',
  },
  {
    label: 'Info',
    value: 'ILMHC',
  },
];

/**
 * Default severity threshold for new integrations
 */
export const DEFAULT_SEVERITY: SeverityLevel = 'MHC'; // Medium, High, Critical

/**
 * Severity threshold descriptions
 */
export const SEVERITY_DESCRIPTIONS: Record<SeverityLevel, string> = {
  C: 'Only notify for Critical severity risks',
  HC: 'Notify for High and Critical severity risks',
  MHC: 'Notify for Medium, High, and Critical severity risks',
  LMHC: 'Notify for Low, Medium, High, and Critical severity risks',
  ILMHC: 'Notify for all severity levels (Info, Low, Medium, High, Critical)',
};

/**
 * Map severity threshold to included levels
 */
export const SEVERITY_THRESHOLD_MAP: Record<SeverityLevel, string[]> = {
  C: ['C'],
  HC: ['H', 'C'],
  MHC: ['M', 'H', 'C'],
  LMHC: ['L', 'M', 'H', 'C'],
  ILMHC: ['I', 'L', 'M', 'H', 'C'],
};

/**
 * Severity colors for UI display
 */
export const SEVERITY_COLORS: Record<string, string> = {
  I: 'text-blue-500',
  L: 'text-yellow-500',
  M: 'text-orange-500',
  H: 'text-red-500',
  C: 'text-purple-600',
};

/**
 * Severity background colors for badges
 */
export const SEVERITY_BG_COLORS: Record<string, string> = {
  I: 'bg-blue-100 dark:bg-blue-900',
  L: 'bg-yellow-100 dark:bg-yellow-900',
  M: 'bg-orange-100 dark:bg-orange-900',
  H: 'bg-red-100 dark:bg-red-900',
  C: 'bg-purple-100 dark:bg-purple-900',
};

/**
 * Get severity label from level code
 *
 * @param level - Severity level code (I, L, M, H, C)
 * @returns Human-readable label
 */
export function getSeverityLabel(level: string): string {
  return SEVERITY_LABELS[level] || level;
}

/**
 * Get severity description from threshold
 *
 * @param threshold - Severity threshold
 * @returns Description of what the threshold includes
 */
export function getSeverityDescription(threshold: SeverityLevel): string {
  return SEVERITY_DESCRIPTIONS[threshold] || '';
}

/**
 * Check if a level meets the threshold
 *
 * @param level - Individual severity level
 * @param threshold - Severity threshold
 * @returns true if level meets or exceeds threshold
 */
export function meetsSeverityThreshold(
  level: string,
  threshold: SeverityLevel
): boolean {
  const includedLevels = SEVERITY_THRESHOLD_MAP[threshold] || [];
  return includedLevels.includes(level);
}

/**
 * Get color class for severity level
 *
 * @param level - Severity level
 * @param background - If true, return background color class
 * @returns Tailwind CSS color class
 */
export function getSeverityColor(level: string, background = false): string {
  return background
    ? SEVERITY_BG_COLORS[level] || ''
    : SEVERITY_COLORS[level] || '';
}
